/**
 * Notification preferences cache manager
 * Stores user preferences for filters and sorting in localStorage
 */

export interface NotificationPreferences {
  sortKey: string;
  sortDirection: 'asc' | 'desc';
  selectedStatuses: string[];
  unreadFilter: boolean;
  viewMode: 'active' | 'archived';
  searchTerm?: string;
}

const CACHE_KEY = 'notification_preferences';
const RETURN_SEARCH_KEY = 'notification_return_search';

/**
 * Save notification preferences to localStorage
 */
export const savePreferences = (prefs: Partial<NotificationPreferences>): void => {
  try {
    const existing = loadPreferences();
    const updated = { ...existing, ...prefs };
    localStorage.setItem(CACHE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to save notification preferences:', error);
  }
};

/**
 * Load notification preferences from localStorage
 */
export const loadPreferences = (): NotificationPreferences => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Failed to load notification preferences:', error);
  }
  
  // Return defaults if no cache or error
  return {
    sortKey: 'created_at',
    sortDirection: 'desc',
    selectedStatuses: [],
    unreadFilter: false,
    viewMode: 'active'
  };
};

/**
 * Clear all notification preferences
 */
export const clearPreferences = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error('Failed to clear notification preferences:', error);
  }
};

/**
 * Save the last notifications overview search for detail العودة.
 */
export const saveReturnSearch = (search: string): void => {
  try {
    sessionStorage.setItem(RETURN_SEARCH_KEY, search);
  } catch (error) {
    console.error('Failed to save notification return search:', error);
  }
};

/**
 * Load the last notifications overview search without clearing it.
 */
export const loadReturnSearch = (): string | null => {
  try {
    return sessionStorage.getItem(RETURN_SEARCH_KEY);
  } catch (error) {
    console.error('Failed to load notification return search:', error);
    return null;
  }
};

/**
 * Load and clear the last notifications overview search.
 */
export const consumeReturnSearch = (): string | null => {
  try {
    const value = sessionStorage.getItem(RETURN_SEARCH_KEY);
    sessionStorage.removeItem(RETURN_SEARCH_KEY);
    return value;
  } catch (error) {
    console.error('Failed to consume notification return search:', error);
    return null;
  }
};

/**
 * Merge cached preferences with URL parameters
 * URL parameters take priority over cached values
 */
export const mergeWithUrlParams = (
  urlParams: URLSearchParams,
  cached: NotificationPreferences
): Partial<NotificationPreferences> => {
  const merged: Partial<NotificationPreferences> = {};

  // searchTerm (q) is bewust niet meer gemerged: het is een vluchtige zoekactie,
  // alleen gestuurd door de URL. Niet herstellen vanuit cache.


  
  // Status from URL only - URL is single source of truth
  const statusParam = urlParams.get('status');
  if (statusParam && statusParam !== 'all') {
    merged.selectedStatuses = statusParam.split(',').filter(Boolean);
  }
  // No cache fallback - if no URL parameter, use empty array (shows all statuses)
  
  // Unread filter from URL or cache
  const unreadParam = urlParams.get('unread');
  if (unreadParam === 'true') {
    merged.unreadFilter = true;
  } else if (!unreadParam && cached.unreadFilter) {
    merged.unreadFilter = cached.unreadFilter;
  }
  
  // View mode from URL or cache
  const viewParam = urlParams.get('view') as 'active' | 'archived' | null;
  if (viewParam) {
    merged.viewMode = viewParam;
  } else if (cached.viewMode) {
    merged.viewMode = cached.viewMode;
  }

  const sortKeyParam = urlParams.get('sort');
  const sortDirectionParam = urlParams.get('dir');
  if (sortKeyParam) {
    merged.sortKey = sortKeyParam;
  } else if (cached.sortKey) {
    merged.sortKey = cached.sortKey;
  }

  if (sortDirectionParam === 'asc' || sortDirectionParam === 'desc') {
    merged.sortDirection = sortDirectionParam;
  } else if (cached.sortDirection) {
    merged.sortDirection = cached.sortDirection;
  }
  
  return merged;
};
